import { useState, useRef, useCallback, useEffect } from 'react'
import { MediaRecorder, IMediaRecorder } from 'extendable-media-recorder'

export const useAudioRecorder = () => {
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)

  const mediaRecorderRef = useRef<IMediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      // Use WAV format for better transcription compatibility
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/wav' })
      mediaRecorderRef.current = recorder
      streamRef.current = stream
      chunksRef.current = []

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/wav' })
        setAudioBlob(blob)
        chunksRef.current = []

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop())
      }

      // No timeslice: ondataavailable fires exactly once before onstop with a
      // single complete WAV blob whose RIFF header declares the full frame
      // count. A timeslice (e.g. start(100)) only writes the header on the
      // first chunk, so concatenated chunks carry a truncated frame count and
      // the backend transcribes only the first ~100ms.
      recorder.start()
      setIsRecording(true)
      setIsPaused(false)
      setAudioBlob(null)
      setRecordingTime(0)

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1)
      }, 1000)

    } catch (error) {
      console.error('Error starting recording:', error)
      throw error
    }
  }, [])

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      setIsPaused(false)
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [isRecording])

  const togglePause = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume()
        setIsPaused(false)
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1)
        }, 1000)
      } else {
        mediaRecorderRef.current.pause()
        setIsPaused(true)
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
      }
    }
  }, [isRecording, isPaused])

  const discardRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
        if (mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop()
        }
    }
    if (streamRef.current) {
        streamRef.current.getTracks().forEach((track: MediaStreamTrack) => track.stop())
        streamRef.current = null
    }
    setIsRecording(false)
    setIsPaused(false)
    setAudioBlob(null)
    setRecordingTime(0)
    if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
    }
  }, [])

  const clearAudioBlob = useCallback(() => {
    setAudioBlob(null)
  }, [])

  // Cleanup on unmount: stop recording, release mic, clear timer
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try { mediaRecorderRef.current.stop() } catch { /* already stopped */ }
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])

  return {
    startRecording,
    stopRecording,
    togglePause,
    discardRecording,
    clearAudioBlob,
    isRecording,
    isPaused,
    recordingTime,
    audioBlob
  }
}

